import React from 'react';
import {
  Inbox, Send, ShieldCheck, Sparkles, HelpCircle,
  MoreHorizontal, CheckCircle2,
} from 'lucide-react';
import {
  countRequestsByStage,
  countActiveAppointments,
  PIPELINE_STAGE_LABELS,
} from '@/lib/contracting/statusMapping';

/**
 * PipelineTiles — the shared 6-tile row rendered by both the Agent and
 * Agency Command Center contracting pipelines. Extracted in Phase 18.6 so
 * both callers share one visual + accessibility contract.
 *
 * Responsibilities (kept intentionally narrow):
 *   • Render the 6 tiles (New / Under Review / Missing Information /
 *     Approved / Other / Activated) from pre-fetched props.
 *   • Compute per-stage counts via the shared status-mapping utility.
 *   • Provide `role="list"` + `role="listitem"` + aria-labels.
 *   • Handle the Other tile's dual state (Unclassified > 0 vs Reconciled = 0).
 *
 * NOT responsibilities:
 *   • Data fetching (parent components own the API calls).
 *   • Layout above/below the tiles (parent renders the surrounding Card).
 *
 * @param {Array} requests      — contract-request records
 * @param {Array} appointments  — carrier-appointment records (for Activated)
 * @param {string} testIdPrefix — 'agent-pipeline' or 'agency-pipeline'
 */
const REQUEST_TILES = [
  { id: 'new',      icon: Inbox },
  { id: 'review',   icon: Send },
  { id: 'missing',  icon: HelpCircle },
  { id: 'approved', icon: ShieldCheck },
];

const StageTile = ({ id, icon: Icon, count, label, testId }) => (
  <div
    role="listitem"
    aria-label={`${label}: ${count} contract request${count === 1 ? '' : 's'}`}
    className="p-3 rounded-lg border text-center"
    style={{
      borderColor: count > 0 ? 'var(--kr-navy-100)' : 'var(--kr-border)',
      background: count > 0 ? 'var(--kr-navy-50)' : '#ffffff',
    }}
    data-testid={testId}
    data-stage-id={id}
  >
    <Icon size={16} className="mx-auto mb-1" style={{ color: count > 0 ? 'var(--kr-navy-700)' : 'var(--kr-subtle)' }} />
    <div className="text-2xl font-heading font-semibold tabular-nums leading-none"
         style={{ color: count > 0 ? 'var(--kr-navy-900)' : 'var(--kr-subtle)' }}>
      {count}
    </div>
    <div className="text-[10px] font-medium uppercase tracking-wider mt-1" style={{ color: 'var(--kr-muted)' }}>
      {label}
    </div>
    <div className="text-[9px] mt-0.5" style={{ color: 'var(--kr-subtle)' }}>Requests</div>
  </div>
);

const OtherTile = ({ count, testId }) => {
  const isReconciled = count === 0;
  return (
    <div
      role="listitem"
      aria-label={
        isReconciled
          ? 'Other: 0 unclassified requests — all requests accounted for'
          : `Other: ${count} unclassified request${count === 1 ? '' : 's'} with statuses not mapped to a pipeline stage`
      }
      title={
        isReconciled
          ? 'All requests are mapped to a pipeline stage.'
          : 'Requests with workflow statuses that are not currently mapped to a pipeline stage.'
      }
      className="p-3 rounded-lg border text-center"
      style={{
        borderColor: isReconciled ? 'var(--kr-border)' : 'var(--kr-gold-300)',
        background: isReconciled ? 'var(--kr-green-50)' : 'var(--kr-gold-50)',
      }}
      data-testid={testId}
      data-stage-id="other"
    >
      {isReconciled ? (
        <CheckCircle2 size={16} className="mx-auto mb-1" style={{ color: 'var(--kr-green-700)' }} />
      ) : (
        <MoreHorizontal size={16} className="mx-auto mb-1" style={{ color: 'var(--kr-gold-600)' }} />
      )}
      <div className="text-2xl font-heading font-semibold tabular-nums leading-none"
           style={{ color: isReconciled ? 'var(--kr-green-700)' : 'var(--kr-navy-900)' }}>
        {count}
      </div>
      <div className="text-[10px] font-medium uppercase tracking-wider mt-1" style={{ color: 'var(--kr-muted)' }}>
        {isReconciled ? 'Reconciled' : 'Unclassified'}
      </div>
      <div className="text-[9px] mt-0.5" style={{ color: 'var(--kr-subtle)' }}>
        {isReconciled ? 'All requests accounted for' : 'Requests'}
      </div>
    </div>
  );
};

const ActivatedTile = ({ count, testId }) => (
  <div
    role="listitem"
    aria-label={`Activated: ${count} active writing number${count === 1 ? '' : 's'}`}
    className="p-3 rounded-lg border text-center"
    style={{
      borderColor: count > 0 ? 'var(--kr-navy-100)' : 'var(--kr-border)',
      background: count > 0 ? 'var(--kr-navy-50)' : '#ffffff',
    }}
    data-testid={testId}
    data-stage-id="activated"
  >
    <Sparkles size={16} className="mx-auto mb-1"
              style={{ color: count > 0 ? 'var(--kr-navy-700)' : 'var(--kr-subtle)' }} />
    <div className="text-2xl font-heading font-semibold tabular-nums leading-none"
         style={{ color: count > 0 ? 'var(--kr-navy-900)' : 'var(--kr-subtle)' }}>
      {count}
    </div>
    <div className="text-[10px] font-medium uppercase tracking-wider mt-1" style={{ color: 'var(--kr-muted)' }}>
      Activated
    </div>
    <div className="text-[9px] mt-0.5" style={{ color: 'var(--kr-subtle)' }}>Active writing numbers</div>
  </div>
);

export const PipelineTiles = ({ requests = [], appointments = [], testIdPrefix }) => {
  const stageCounts = countRequestsByStage(requests);
  const activatedCount = countActiveAppointments(appointments);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" role="list" aria-label="Contracting pipeline stages">
      {REQUEST_TILES.map((s) => (
        <StageTile
          key={s.id}
          id={s.id}
          icon={s.icon}
          count={stageCounts[s.id]}
          label={PIPELINE_STAGE_LABELS[s.id]}
          testId={`${testIdPrefix}-${s.id}`}
        />
      ))}
      <OtherTile count={stageCounts.other} testId={`${testIdPrefix}-other`} />
      <ActivatedTile count={activatedCount} testId={`${testIdPrefix}-activated`} />
    </div>
  );
};

export default PipelineTiles;
